import { Spacer } from "@hackthenorth/north";
import { TextArea, InlineError } from "north.js";
import React from "react";
import styled from "styled-components";

import { FormFieldLabelRow, OptionalLabel } from "src/shared/components";
import { InfoIcon } from "src/static/icons";
import {
  weekendPaceBellPepper,
  weekendPaceChili,
  weekendPaceChiliFire,
} from "src/static/img";

import {
  MAX_TEAM_SIZE,
  TEAMMATE_CRITERIA_MAX_CHARS,
  TEAMMATES_NEEDED_OPTIONS,
  WEEKEND_PACE_OPTIONS,
} from "../constants";
import ProfileDropdownMenu from "../ProfileDropdownMenu";
import {
  captionTextStyles,
  fieldLabelStyles,
  subtitleTextStyles,
  textAreaStyles,
} from "../styles";
import { ProfileFormMode, ProfileFormValues } from "../types";

import Teammates from "./Teammates";

export interface TeamFieldsProps {
  staging: ProfileFormValues;
  setStagingField: <K extends keyof ProfileFormValues>(
    field: K,
    value: ProfileFormValues[K]
  ) => void;
  showErrors: boolean;
  showInfoBanner?: boolean;
  mode: ProfileFormMode;
}

const TeamFields: React.FC<TeamFieldsProps> = ({
  staging,
  setStagingField,
  showErrors,
  showInfoBanner = true,
  mode,
}) => {
  const showConfirmedTeammates = mode === "standalone";
  const teammatesNeeded = staging.teammates_needed;
  const showTeammateCriteria = teammatesNeeded != null && teammatesNeeded > 0;
  const teammatesNeededLabel = TEAMMATES_NEEDED_OPTIONS.find(
    (option) => option.value === teammatesNeeded
  )?.label;
  const teammatesNeededError =
    showErrors && teammatesNeeded == null
      ? "Please select how many teammates you're looking for."
      : undefined;

  return (
    <>
      <Heading>
        {showConfirmedTeammates ? "Your teammates" : "Teammates"}
      </Heading>

      {showConfirmedTeammates && (
        <QuestionWrapper>
          <FormFieldLabelRow>
            <FieldLabel>Search and add your confirmed team members.</FieldLabel>
            <OptionalLabel>Optional</OptionalLabel>
          </FormFieldLabelRow>
          <Spacer height={16} />
          <Teammates staging={staging} setStagingField={setStagingField} />
        </QuestionWrapper>
      )}

      <QuestionWrapper>
        <FieldLabel>How many teammates are you still looking for?</FieldLabel>
        <Spacer height={4} />
        <HelperText>
          Teams of up to {MAX_TEAM_SIZE} hackers are permitted.
        </HelperText>
        <Spacer height={12} />
        <TeammatesDropdownWrapper>
          <ProfileDropdownMenu
            items={[
              TEAMMATES_NEEDED_OPTIONS.map((option) => ({
                content: <TeammatesItemText>{option.label}</TeammatesItemText>,
                selected: option.value === teammatesNeeded,
                action: () => setStagingField("teammates_needed", option.value),
              })),
            ]}
            hasError={!!teammatesNeededError}
            describedBy={
              teammatesNeededError ? "teammates-needed-error" : undefined
            }
          >
            <TeammatesTriggerText isPlaceholder={!teammatesNeededLabel}>
              {teammatesNeededLabel || "Select an option"}
            </TeammatesTriggerText>
            <ChevronDown />
          </ProfileDropdownMenu>
        </TeammatesDropdownWrapper>
        {teammatesNeededError && (
          <>
            <Spacer height={4} />
            <InlineError id="teammates-needed-error">
              {teammatesNeededError}
            </InlineError>
          </>
        )}
        {showInfoBanner && (
          <>
            <Spacer height={12} />
            <InfoBanner>
              <StyledInfoIcon width={16} height={16} />
              <InfoText>
                Once the directory opens, you can add your confirmed teammates
                under your profile settings.
              </InfoText>
            </InfoBanner>
          </>
        )}
      </QuestionWrapper>

      <QuestionWrapper>
        <PaceLabelRow>
          <FieldLabel>Your weekend pace</FieldLabel>
          <OptionalLabel>Optional</OptionalLabel>
          <TapHint>Tap to select</TapHint>
        </PaceLabelRow>
        <Spacer height={4} />
        <HelperText>
          Match your personal intensity level with the type of team flow
          you&rsquo;re looking for.
        </HelperText>
        <Spacer height={12} />
        <PaceGrid role="group" aria-label="Weekend pace">
          {WEEKEND_PACE_OPTIONS.map((option) => {
            const isSelected = staging.weekend_pace === option.value;
            const iconSrc = option.useBellPepper
              ? weekendPaceBellPepper
              : option.useFire
              ? weekendPaceChiliFire
              : weekendPaceChili;

            return (
              <PaceOptionWrapper key={option.value}>
                <PaceTooltip role="tooltip">{option.tooltip}</PaceTooltip>
                <PaceOption
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={`${option.label}. ${option.tooltip}${
                    isSelected ? " Selected; activate again to clear." : ""
                  }`}
                  isSelected={isSelected}
                  onClick={() =>
                    setStagingField(
                      "weekend_pace",
                      isSelected ? null : option.value
                    )
                  }
                >
                  <ChiliRow>
                    {Array.from({ length: option.chiliCount }, (_, i) => (
                      <ChiliIcon key={i} src={iconSrc} alt="" aria-hidden />
                    ))}
                  </ChiliRow>
                  <PaceLabel>{option.label}</PaceLabel>
                </PaceOption>
              </PaceOptionWrapper>
            );
          })}
        </PaceGrid>
      </QuestionWrapper>

      {showTeammateCriteria && (
        <QuestionWrapper>
          <FormFieldLabelRow>
            <FieldLabel>What are you looking for in teammates?</FieldLabel>
            <OptionalLabel>Optional</OptionalLabel>
          </FormFieldLabelRow>
          <Spacer height={12} />
          <TextAreaStyled
            placeholder="Start typing here..."
            rows={3}
            fullWidth
            maxLength={TEAMMATE_CRITERIA_MAX_CHARS}
            value={staging.teammate_criteria ?? ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setStagingField("teammate_criteria", e.target.value)
            }
          />
          <CharCount>
            {(staging.teammate_criteria ?? "").length}/
            {TEAMMATE_CRITERIA_MAX_CHARS} chars
          </CharCount>
        </QuestionWrapper>
      )}
    </>
  );
};

export default TeamFields;

const Heading = styled.h3`
  font-size: 20px;
  font-weight: 900;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin-bottom: 24px;
`;

const QuestionWrapper = styled.div`
  width: 100%;
  margin-bottom: 36px;
`;

const FieldLabel = styled.p`
  ${fieldLabelStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

const HelperText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  margin: 0;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
  border-radius: 8px;
`;

const StyledInfoIcon = styled(InfoIcon)`
  flex-shrink: 0;
`;

const InfoText = styled.p`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  margin: 0;
`;

const TeammatesDropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const TeammatesTriggerText = styled.span<{ isPlaceholder: boolean }>`
  ${subtitleTextStyles}
  color: ${({ theme, isPlaceholder }) =>
    isPlaceholder
      ? theme.globalConstants.color.textTertiary
      : theme.globalConstants.color.textBody};
`;

const TeammatesItemText = styled.span`
  ${subtitleTextStyles}
`;

const ChevronDownIcon = styled.svg`
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  flex-shrink: 0;
`;

const ChevronDown = () => (
  <ChevronDownIcon
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width={16}
    height={16}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
    />
  </ChevronDownIcon>
);

const PaceLabelRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const TapHint = styled.span`
  ${subtitleTextStyles}
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const PaceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;

  ${({ theme }) => theme.mediaQueries.large`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}
`;

const PaceTooltip = styled.div`
  box-sizing: border-box;
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  width: 210px;
  min-height: 79px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0;
  text-align: center;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.15s ease, visibility 0.15s ease;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 7px solid transparent;
    border-top-color: ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  &::before {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: -1px;
    border: 6px solid transparent;
    border-top-color: ${({ theme }) => theme.globalConstants.color.white};
    z-index: 1;
  }
`;

const PaceOptionWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;

  &:hover ${PaceTooltip} {
    opacity: 1;
    visibility: visible;
  }

  &:focus-within ${PaceTooltip} {
    opacity: 1;
    visibility: visible;
  }
`;

const PaceOption = styled.button<{ isSelected: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: 68px;
  padding: 14px 12px;
  border-radius: 4px;
  cursor: pointer;
  box-shadow: 0px 1px 2px 0px #0000000d;
  border: 1px solid
    ${({ theme, isSelected }) =>
      isSelected
        ? theme.globalConstants.color.borderPrimary
        : theme.globalConstants.color.borderGray};
  background: ${({ theme, isSelected }) =>
    isSelected
      ? theme.globalConstants.color.backgroundPrimaryLight
      : theme.globalConstants.color.white};
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }
`;

const ChiliRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const ChiliIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const PaceLabel = styled.span`
  ${subtitleTextStyles}
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
  color: ${({ theme }) => theme.globalConstants.color.borderPrimary};
`;

const TextAreaStyled = styled(TextArea)`
  ${textAreaStyles}
`;

const CharCount = styled.p`
  ${captionTextStyles}
  margin: 4px 0 0;
  text-align: right;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;
